import * as APIUtils from '@/components/Globals/Utils';

// Types
export interface CustomFieldHistoriesRequest {
  CustomFieldValueId: number;
  CustomFieldId: number;
  CoreTaskId: number;
  Start: string;
  End: string;
}

export interface MultipleCustomFieldHistoriesRequest {
  ConversationId: number;
  Start: string;
  End: string;
}

export interface UpdateCustomFieldHistoriesRequest {
  CustomFieldDashboardId: number;
  Value: string;
}

export interface NewCustomFieldHistoriesRequest {
  CreatedAt: string;
  CustomFieldDashboardId: number;
  Value: string;
  CustomFieldValueId?: number;
  CustomFieldId: number;
  CoreTaskId: number;
  DashboardId: string;
}

export interface CustomFieldValueRequest {
  CoreTaskID: number;
  ProjectId: number;
  PersonId: number;
  CustomFieldId: number;
  CustomFieldValueId?: number;
  Description: string;
  FieldTypeId: number;
  LayerId: number;
  Name: string;
  Value: string;
}

export interface DashboardsByCoreTaskIdRequest {
  CoreTaskId: number;
}

export interface ApiRequestConfig<T = any> {
  api: any;
  request?: T;
}

export interface GetListRequestConfig extends ApiRequestConfig {
  CustomFieldValueId: number;
  CustomFieldId: number;
  CoreTaskId: number;
  Start: string;
  End: string;
}

export interface GetMultipleListRequestConfig extends ApiRequestConfig {
  ConversationId: number;
  Start: string;
  End: string;
}

export interface UpdateRequestConfig extends ApiRequestConfig<UpdateCustomFieldHistoriesRequest> {}

export interface NewRequestConfig extends ApiRequestConfig<NewCustomFieldHistoriesRequest> {}

export interface SaveRequestConfig extends ApiRequestConfig<CustomFieldValueRequest> {}

export interface GetDashboardsRequestConfig extends ApiRequestConfig {
  CoreTaskId: number;
}

// Response types
export interface CustomFieldHistory {
  CustomFieldDashboardId: number;
  CustomFieldId: number;
  CoreTaskId: number;
  Name: string;
  LayerId: number;
  CreatedAt: string;
  Value: string;
  Last: number;
}

export interface CustomFieldInfo {
  CustomFieldId: number;
  CustomFieldValueId?: number;
  FieldTypeId: number;
  LayerId: number;
  LayerName: string;
  Name: string;
  CoreTaskId: number;
  ProjectId: number;
  PersonId: number;
  Description: string;
}

export interface MultipleHistoriesResponse {
  ListValues: CustomFieldHistory[];
  ListCustomFields: CustomFieldInfo[];
}

export interface SaveResponse {
  CustomFieldValueId: number;
}

export interface NewHistoriesResponse {
  CustomFieldDashboardId: number;
}

export interface Dashboard {
  DashboardId: string;
  Name: string;
}

/**
 * Service for managing edit history and custom field operations
 */
class EditHistoryService
{
  /**
   * Retrieves list of custom field histories
   */
  static async getListCustomFieldHistories(
    requestConfig: GetListRequestConfig,
  ): Promise<CustomFieldHistory[]>
  {
    const params = new URLSearchParams({
      CustomFieldValueId: requestConfig.CustomFieldValueId.toString(),
      CustomFieldId: requestConfig.CustomFieldId.toString(),
      CoreTaskId: requestConfig.CoreTaskId.toString(),
      Start: requestConfig.Start,
      End: requestConfig.End,
    });

    return APIUtils.callFetch(
      requestConfig.api,
      `api/CustomField/GetListCustomFieldsHistories?${params.toString()}`,
      'GET',
    );
  }

  /**
   * Retrieves list of multiple custom field histories for a conversation
   */
  static async getListMultipleCustomFieldHistories(
    requestConfig: GetMultipleListRequestConfig,
  ): Promise<MultipleHistoriesResponse>
  {
    const params = new URLSearchParams({
      ConversationId: requestConfig.ConversationId.toString(),
      Start: requestConfig.Start,
      End: requestConfig.End,
    });

    return APIUtils.callFetch(
      requestConfig.api,
      `api/CustomField/GetListMultipleCustomFieldsHistories?${params.toString()}`,
      'GET',
    );
  }

  /**
   * Updates existing custom field histories
   */
  static async updateCustomFieldHistories(requestConfig: UpdateRequestConfig): Promise<void>
  {
    return APIUtils.callFetch(
      requestConfig.api,
      'api/CustomField/UpdateCustomFieldHistories',
      'POST',
      requestConfig.request,
      true,
    );
  }

  /**
   * Creates new custom field histories
   */
  static async newCustomFieldHistories(
    requestConfig: NewRequestConfig,
  ): Promise<NewHistoriesResponse>
  {
    return APIUtils.callFetch(
      requestConfig.api,
      'api/CustomField/NewCustomFieldHistories',
      'POST',
      requestConfig.request,
    );
  }

  /**
   * Updates an existing custom field value
   */
  static async save(requestConfig: SaveRequestConfig): Promise<void>
  {
    return APIUtils.callFetch(
      requestConfig.api,
      'api/customfieldvalue/',
      'PUT',
      requestConfig.request,
      true,
    );
  }

  /**
   * Creates a new custom field value
   */
  static async savePost(requestConfig: SaveRequestConfig): Promise<SaveResponse>
  {
    return APIUtils.callFetch(
      requestConfig.api,
      'api/customfieldvalue/',
      'POST',
      requestConfig.request,
    );
  }

  /**
   * Retrieves dashboards associated with a core task
   */
  static async getDashboardsByCoreTaskId(
    requestConfig: GetDashboardsRequestConfig,
  ): Promise<Dashboard[]>
  {
    const params = new URLSearchParams({
      coreTaskId: requestConfig.CoreTaskId.toString(),
    });

    return APIUtils.callFetch(
      requestConfig.api,
      `api/dashboard/GetDashboardsByCoreTaskId?${params.toString()}`,
      'GET',
    );
  }
}

export default EditHistoryService;
